// ============================================================
// src/shack/shackFrames.js
// Marcos, certificados, mapas y texturas del Shack
// ============================================================

import {
  T,
  MAPA_YAESU_URL,
  FRAME_MODEL_PATHS,
  FRAME_GEOMETRY,
  MIN_TONE,
  MAX_TONE,
} from './shackConfig.js';

import {
  crearShadowTexture,
  crearCertificateTexture,
} from './shackUtils.js';

const SEG_FRAME_X = 18;
const SEG_FRAME_Y = 14;
const SEG_INNER_X = 14;
const SEG_INNER_Y = 10;

// Textura de vidrio compartida entre todos los certificados (idéntica para todos)
let _glassTex = null;

// ------------------------------------------------------------
// COMPOSITOR 2D DE CERTIFICADOS CON MARCO
// ------------------------------------------------------------

export async function generarCertificadoConMarco(imgCert, modelo = 'black') {
  const frameUrl = FRAME_MODEL_PATHS[modelo];

  if (!frameUrl) return imgCert;

  const frameImg = new Image();
  frameImg.src = frameUrl;
  await frameImg.decode();

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = frameImg.width;
  canvas.height = frameImg.height;

  ctx.drawImage(frameImg, 0, 0);

  const { certRect } = FRAME_GEOMETRY;
  const dstX = Math.round(frameImg.width  * certRect.x);
  const dstY = Math.round(frameImg.height * certRect.y);
  const dstW = Math.round(frameImg.width  * certRect.w);
  const dstH = Math.round(frameImg.height * certRect.h);

  ctx.drawImage(imgCert, dstX, dstY, dstW, dstH);

  ctx.save();
  ctx.beginPath();
  ctx.rect(dstX, dstY, dstW, dstH);
  ctx.clip();
  aplicarTexturaPapelCertificado(ctx, frameImg.width, frameImg.height, 0.72);
  aplicarReflejoVidrio(ctx, dstX, dstY, dstW, dstH, 0.66);
  ctx.restore();

  return canvas;
}

// ------------------------------------------------------------
// HELPERS INTERNOS
// ------------------------------------------------------------

function clampNum(valor, min, max) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return min;
  return Math.min(Math.max(n, min), max);
}

function elegirFrameModelKey(tone) {
  const t = clampNum(tone ?? 0.55, MIN_TONE, MAX_TONE);
  return t <= 0.38 ? 'black' : 'madera';
}

function guardarBasePositions(mesh) {
  if (!mesh?.geometry?.attributes?.position) return mesh;
  mesh.userData.basePositions = Float32Array.from(mesh.geometry.attributes.position.array);
  return mesh;
}

function marcarWarp(mesh, itemKey, offsetX, offsetY, zOffset = 0) {
  mesh.userData.itemKey = itemKey;
  mesh.userData.surfaceWarp = { offsetX, offsetY, zOffset };
  return guardarBasePositions(mesh);
}

function obtenerFrameModelTexture(frameKey, frameModelTextureCache, renderer, onUpdate) {
  const THREE = T();

  if (frameModelTextureCache.has(frameKey)) {
    return frameModelTextureCache.get(frameKey);
  }

  const url = FRAME_MODEL_PATHS[frameKey] || FRAME_MODEL_PATHS.black;
  const tex = new THREE.Texture();
  tex.needsUpdate = false;

  const img = new Image();
  img.onload = () => {
    const off = document.createElement('canvas');
    off.width = img.naturalWidth;
    off.height = img.naturalHeight;

    const octx = off.getContext('2d');
    octx.drawImage(img, 0, 0);

    const idata = octx.getImageData(0, 0, off.width, off.height);
    const d = idata.data;
    const W = off.width;
    const H = off.height;
    const visited = new Uint8Array(W * H);
    const qx = [Math.floor(W / 2)];
    const qy = [Math.floor(H / 2)];
    let head = 0;

    while (head < qx.length) {
      const x = qx[head];
      const y = qy[head];
      head++;

      if (x < 0 || x >= W || y < 0 || y >= H) continue;

      const vi = y * W + x;
      if (visited[vi]) continue;
      visited[vi] = 1;

      const pi = vi * 4;
      if (d[pi] > 30 || d[pi + 1] > 30 || d[pi + 2] > 30) continue;

      d[pi + 3] = 0;

      qx.push(x + 1, x - 1, x, x);
      qy.push(y, y, y + 1, y - 1);
    }

    octx.putImageData(idata, 0, 0);

    tex.image = off;
    tex.needsUpdate = true;

    if (renderer?.capabilities?.getMaxAnisotropy) {
      tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
    }

    if (typeof onUpdate === 'function') onUpdate();
  };

  img.src = url;
  frameModelTextureCache.set(frameKey, tex);
  return tex;
}

function crearRngDeterministico(seed = 1) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return function rng() {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function aplicarTexturaPapelCertificado(ctx, width, height, intensidad = 1) {
  const rng = crearRngDeterministico(93017);
  const grainAlpha = 0.018 * intensidad;
  const fibras = Math.round(180 * intensidad);

  ctx.save();
  ctx.globalCompositeOperation = 'multiply';

  for (let i = 0; i < fibras; i++) {
    const x = rng() * width;
    const y = rng() * height;
    const len = (10 + rng() * 42) * (width / 1600);
    const ang = (rng() * 0.55 - 0.275) + (rng() > 0.5 ? 0 : Math.PI);
    const alpha = 0.010 + rng() * grainAlpha;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(ang);
    ctx.strokeStyle = `rgba(120,105,80,${alpha})`;
    ctx.lineWidth = Math.max(0.35, (0.45 + rng() * 0.8) * (width / 1600));
    ctx.beginPath();
    ctx.moveTo(-len / 2, 0);
    ctx.lineTo(len / 2, 0);
    ctx.stroke();
    ctx.restore();
  }

  const img = ctx.getImageData(0, 0, width, height);
  const data = img.data;
  for (let i = 0; i < data.length; i += 4) {
    const n = Math.round((rng() - 0.5) * 10 * intensidad);
    data[i] = Math.max(0, Math.min(255, data[i] + n));
    data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + n));
    data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + n));
  }
  ctx.putImageData(img, 0, 0);

  const gAge = ctx.createRadialGradient(
    width / 2, height / 2, Math.min(width, height) * 0.18,
    width / 2, height / 2, Math.max(width, height) * 0.72
  );
  gAge.addColorStop(0, 'rgba(255,255,255,0.00)');
  gAge.addColorStop(1, `rgba(110,82,38,${0.055 * intensidad})`);
  ctx.fillStyle = gAge;
  ctx.fillRect(0, 0, width, height);

  const edge = Math.max(8, Math.min(width, height) * 0.045);

  const gTop = ctx.createLinearGradient(0, 0, 0, edge);
  gTop.addColorStop(0, `rgba(0,0,0,${0.055 * intensidad})`);
  gTop.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gTop;
  ctx.fillRect(0, 0, width, edge);

  const gLeft = ctx.createLinearGradient(0, 0, edge, 0);
  gLeft.addColorStop(0, `rgba(0,0,0,${0.040 * intensidad})`);
  gLeft.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gLeft;
  ctx.fillRect(0, 0, edge, height);

  ctx.restore();
}

function aplicarReflejoVidrio(ctx, x, y, width, height, intensidad = 1) {
  const alphaBase = 0.055 * intensidad;
  const alphaLine = 0.030 * intensidad;
  const alphaEdge = 0.040 * intensidad;

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.clip();

  const diagonal = ctx.createLinearGradient(x, y, x + width, y + height);
  diagonal.addColorStop(0.00, 'rgba(255,255,255,0)');
  diagonal.addColorStop(0.18, `rgba(255,255,255,${alphaBase})`);
  diagonal.addColorStop(0.34, 'rgba(255,255,255,0)');
  diagonal.addColorStop(0.66, 'rgba(255,255,255,0)');
  diagonal.addColorStop(0.82, `rgba(255,255,255,${alphaBase * 0.45})`);
  diagonal.addColorStop(1.00, 'rgba(255,255,255,0)');

  ctx.globalCompositeOperation = 'screen';
  ctx.fillStyle = diagonal;
  ctx.fillRect(x, y, width, height);

  ctx.globalCompositeOperation = 'source-over';

  const bandaW = width * 0.26;
  const bandaH = height * 1.35;

  ctx.save();
  ctx.translate(x + width * 0.23, y + height * 0.48);
  ctx.rotate(-0.42);
  const banda = ctx.createLinearGradient(-bandaW / 2, 0, bandaW / 2, 0);
  banda.addColorStop(0.00, 'rgba(255,255,255,0)');
  banda.addColorStop(0.42, `rgba(255,255,255,${alphaLine})`);
  banda.addColorStop(0.58, `rgba(255,255,255,${alphaLine * 0.72})`);
  banda.addColorStop(1.00, 'rgba(255,255,255,0)');
  ctx.fillStyle = banda;
  ctx.fillRect(-bandaW / 2, -bandaH / 2, bandaW, bandaH);
  ctx.restore();

  const borde = ctx.createLinearGradient(x, y, x, y + height);
  borde.addColorStop(0.00, `rgba(255,255,255,${alphaEdge})`);
  borde.addColorStop(0.10, 'rgba(255,255,255,0)');
  borde.addColorStop(0.88, 'rgba(0,0,0,0)');
  borde.addColorStop(1.00, `rgba(0,0,0,${alphaEdge * 0.55})`);

  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = borde;
  ctx.fillRect(x, y, width, height);

  ctx.restore();
}

function crearTexturaVidrio(width = 1024, height = 768, intensidad = 1) {
  const THREE = T();

  const cv = document.createElement('canvas');
  cv.width = width;
  cv.height = height;

  const ctx = cv.getContext('2d');
  aplicarReflejoVidrio(ctx, 0, 0, width, height, intensidad);

  const tex = new THREE.CanvasTexture(cv);
  tex.needsUpdate = true;

  return tex;
}

function crearTexturaDesdeImagen(baseImage, renderer, opciones = {}) {
  const THREE = T();
  const maxTex = renderer?.capabilities?.maxTextureSize || 16384;
  const scale = Math.min(1, maxTex / Math.max(baseImage.naturalWidth, baseImage.naturalHeight));
  const texW = Math.max(1, Math.round(baseImage.naturalWidth * scale));
  const texH = Math.max(1, Math.round(baseImage.naturalHeight * scale));

  const cv = document.createElement('canvas');
  cv.width = texW;
  cv.height = texH;

  const ctx = cv.getContext('2d');
  ctx.drawImage(baseImage, 0, 0, texW, texH);

  if (opciones.papel) {
    aplicarTexturaPapelCertificado(ctx, texW, texH, opciones.intensidadPapel ?? 0.85);
  }

  const tex = new THREE.CanvasTexture(cv);
  if (renderer?.capabilities?.getMaxAnisotropy) {
    tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
  }
  return { tex, ratio: texW / texH };
}

function crearInnerMesh({
  width,
  height,
  texture,
  itemKey,
  offsetX,
  offsetY,
  zOffset,
  zoomable = false,
}) {
  const THREE = T();
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height, SEG_INNER_X, SEG_INNER_Y),
    new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide })
  );
  if (zoomable) mesh.userData.zoomable = true;
  return marcarWarp(mesh, itemKey, offsetX, offsetY, zOffset);
}

function crearGlassMesh({
  width,
  height,
  itemKey,
  offsetX,
  offsetY,
  zOffset,
}) {
  const THREE = T();

  if (!_glassTex) {
    _glassTex = crearTexturaVidrio(1024, 768, 0.58);
  }

  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height, SEG_INNER_X, SEG_INNER_Y),
    new THREE.MeshBasicMaterial({
      map: _glassTex,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
  );

  mesh.userData.isGlassMesh = true;
  return marcarWarp(mesh, itemKey, offsetX, offsetY, zOffset);
}

function crearFrameMesh({ width, height, texture, itemKey, zOffset, zoomable = false }) {
  const THREE = T();
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height, SEG_FRAME_X, SEG_FRAME_Y),
    new THREE.MeshBasicMaterial({ map: texture, transparent: false, side: THREE.DoubleSide })
  );
  mesh.userData.isFrameMesh = true;
  if (zoomable) mesh.userData.zoomable = true;
  return marcarWarp(mesh, itemKey, 0, 0, zOffset);
}

function crearShadowMesh({ width, height, itemKey }) {
  const THREE = T();
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height, SEG_FRAME_X, SEG_FRAME_Y),
    new THREE.MeshBasicMaterial({
      map: crearShadowTexture(T, 512),
      transparent: true,
      opacity: 0.30,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
  );
  return marcarWarp(mesh, itemKey, 0.014, -0.014, -0.0015);
}

function crearClavo({ width, height, itemKey }) {
  const THREE = T();
  const nail = new THREE.Mesh(
    new THREE.SphereGeometry(Math.max(0.003, width * 0.007), 10, 10),
    new THREE.MeshStandardMaterial({
      color: 0x6a6058,
      roughness: 0.42,
      metalness: 0.40,
    })
  );
  nail.userData.itemKey = itemKey;
  nail.userData.surfaceNail = {
    x: 0,
    y: (height / 2) + 0.005,
    zOffset: 0.004,
  };
  return nail;
}

function aplicarCropSiCorresponde(texture, crop) {
  const THREE = T();
  if (!crop || !(texture instanceof THREE.Texture)) return;
  const { t, r, b, l } = crop;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.offset.set(l, b);
  texture.repeat.set(1 - l - r, 1 - t - b);
  texture.needsUpdate = true;
}

// ------------------------------------------------------------
// GRUPOS 3D
// ------------------------------------------------------------

export function crearCertificadoGroup({
  item,
  baseImage,
  frameModelTextureCache,
  renderer,
  pickables,
  aplicarTransformItem,
  onFrameTextureUpdate,
}) {
  const THREE = T();

  const W = item.w;
  const { certRect } = FRAME_GEOMETRY;
  const frameRatio = item.frameRatio
    ?? (item.imgRatio ? item.imgRatio * (certRect.h / certRect.w) : null)
    ?? null;
  const H = (frameRatio && frameRatio > 0) ? item.w / frameRatio : item.h;

  const group = new THREE.Group();
  group.userData.itemKey = item.shackKey;

  const tone = clampNum(item.frameTone ?? 0.55, MIN_TONE, MAX_TONE);
  const frameKey = item.frameModel ?? elegirFrameModelKey(tone);
  const frameTex = obtenerFrameModelTexture(frameKey, frameModelTextureCache, renderer, onFrameTextureUpdate);
  if (!frameTex) return group;

  const autoCertW   = W * certRect.w;
  const autoCertH   = H * certRect.h;
  const autoCertOffX = (certRect.x + certRect.w * 0.5 - 0.5) * W;
  const autoCertOffY = (0.5 - certRect.y - certRect.h * 0.5) * H;

  let certTex;

  if (baseImage instanceof HTMLImageElement && baseImage.complete && baseImage.naturalWidth) {
    certTex = crearTexturaDesdeImagen(baseImage, renderer, { papel: true, intensidadPapel: 0.86 }).tex;
  } else {
    const maxTex = renderer?.capabilities?.maxTextureSize || 16384;
    const texW = Math.min(maxTex, 4096);
    const texH = item.imgRatio
      ? Math.max(1, Math.round(texW / item.imgRatio))
      : Math.max(1, Math.round(texW * (certRect.h / certRect.w)));
    certTex = crearCertificateTexture(T, item, baseImage, texW, texH);
    if (renderer?.capabilities?.getMaxAnisotropy) {
      certTex.anisotropy = renderer.capabilities.getMaxAnisotropy();
    }
  }

  const cs = (item.certState?.wRel > 0) ? item.certState : null;
  let certW, certH, certOffX, certOffY;

  if (cs) {
    certW    = cs.wRel * W;
    certH    = cs.hRel * H;
    certOffX = (cs.xRel + cs.wRel / 2 - 0.5) * W;
    certOffY = (0.5 - cs.yRel - cs.hRel / 2) * H;
  } else {
    certW    = autoCertW;
    certH    = autoCertH;
    certOffX = autoCertOffX;
    certOffY = autoCertOffY;
  }

  aplicarCropSiCorresponde(certTex, cs?.crop);

  const shadow = crearShadowMesh({ width: W * 1.16, height: H * 1.16, itemKey: item.shackKey });
  group.add(shadow);

  const cert = crearInnerMesh({
    width: certW,
    height: certH,
    texture: certTex,
    itemKey: item.shackKey,
    offsetX: certOffX,
    offsetY: certOffY,
    zOffset: 0.0022,
  });
  group.add(cert);
  if (pickables) pickables.push(cert);

  const vidrio = crearGlassMesh({
    width: certW,
    height: certH,
    itemKey: item.shackKey,
    offsetX: certOffX,
    offsetY: certOffY,
    zOffset: 0.0028,
  });
  group.add(vidrio);

  const frente = crearFrameMesh({
    width: W,
    height: H,
    texture: frameTex,
    itemKey: item.shackKey,
    zOffset: 0.0012,
  });
  group.add(frente);
  if (pickables) pickables.push(frente);

  group.add(crearClavo({ width: W, height: H, itemKey: item.shackKey }));

  if (typeof aplicarTransformItem === 'function') {
    aplicarTransformItem(group, item);
  }

  return group;
}

export function crearMapaGroup({
  item,
  baseImage,
  frameModelTextureCache,
  renderer,
  pickables,
  aplicarTransformItem,
  onFrameTextureUpdate,
}) {
  const THREE = T();

  const W = item.w;
  const frameRatio = item.frameRatio ?? item.ratio ?? (1792 / 1224);
  const H = (frameRatio && frameRatio > 0) ? W / frameRatio : item.h;

  const group = new THREE.Group();
  group.userData.itemKey = item.shackKey;

  const tone = clampNum(item.frameTone ?? 0.10, MIN_TONE, MAX_TONE);
  const frameKey = item.frameModel ?? elegirFrameModelKey(tone);
  const frameTex = obtenerFrameModelTexture(frameKey, frameModelTextureCache, renderer, onFrameTextureUpdate);

  const { certRect: mapRect } = FRAME_GEOMETRY;
  const innerW = W * mapRect.w;
  const innerH = H * mapRect.h;
  const innerCx = (mapRect.x + mapRect.w * 0.5 - 0.5) * W;
  const innerCy = (0.5 - (mapRect.y + mapRect.h * 0.5)) * H;

  const cs = item.certState;
  let mapW;
  let mapH;
  let mapOffX;
  let mapOffY;

  if (cs) {
    mapW = cs.wRel * W;
    mapH = cs.hRel * H;
    mapOffX = (cs.xRel + cs.wRel / 2 - 0.5) * W;
    mapOffY = (0.5 - cs.yRel - cs.hRel / 2) * H;
  } else {
    mapW = innerW;
    mapH = innerH;
    mapOffX = innerCx;
    mapOffY = innerCy;
  }

  let mapTex;
  if (baseImage instanceof HTMLImageElement && baseImage.complete && baseImage.naturalWidth) {
    mapTex = crearTexturaDesdeImagen(baseImage, renderer).tex;
  } else {
    mapTex = new THREE.TextureLoader().load(
      item.src || MAPA_YAESU_URL,
      (tex) => {
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.needsUpdate = true;
      },
      undefined,
      (err) => console.error('[Shack][mapa] error cargando textura:', err)
    );
  }

  aplicarCropSiCorresponde(mapTex, cs?.crop);

  const shadow = crearShadowMesh({ width: W * 1.16, height: H * 1.16, itemKey: item.shackKey });
  group.add(shadow);

  const mapMesh = crearInnerMesh({
    width: mapW,
    height: mapH,
    texture: mapTex,
    itemKey: item.shackKey,
    offsetX: mapOffX,
    offsetY: mapOffY,
    zOffset: 0.0022,
    zoomable: true,
  });
  group.add(mapMesh);
  if (pickables) pickables.push(mapMesh);

  const frente = crearFrameMesh({
    width: W,
    height: H,
    texture: frameTex,
    itemKey: item.shackKey,
    zOffset: 0.0012,
    zoomable: true,
  });
  group.add(frente);
  if (pickables) pickables.push(frente);

  group.add(crearClavo({ width: W, height: H, itemKey: item.shackKey }));

  if (typeof aplicarTransformItem === 'function') {
    aplicarTransformItem(group, item);
  }

  return group;
}

// ------------------------------------------------------------
// EXPORTS AUXILIARES
// ------------------------------------------------------------

export function limpiarCacheFrameTextures(frameModelTextureCache) {
  if (!frameModelTextureCache) return;
  frameModelTextureCache.forEach((tex) => tex?.dispose?.());
  frameModelTextureCache.clear();
}

// ------------------------------------------------------------
// COMPOSITOR 2D — cuadro completo para fullscreen
// ------------------------------------------------------------

export async function componerCuadroCanvas(item, imgOrUrl) {
  let imgEl = null;

  if (imgOrUrl instanceof HTMLImageElement && imgOrUrl.complete && imgOrUrl.naturalWidth > 0) {
    imgEl = imgOrUrl;
  } else if (typeof imgOrUrl === 'string' && imgOrUrl) {
    const img = new Image();
    img.src = imgOrUrl;
    await img.decode().catch(() => {});
    if (img.naturalWidth > 0) imgEl = img;
  }

  const modelo = item.frameModel ?? 'black';
  const frameUrl = FRAME_MODEL_PATHS[modelo] ?? FRAME_MODEL_PATHS.black;
  const frameImg = new Image();
  frameImg.src = frameUrl;
  await frameImg.decode();

  const FW_FRAME = frameImg.naturalWidth;
  const FH_FRAME = frameImg.naturalHeight;
  const { certRect } = FRAME_GEOMETRY;

  // Escalar el canvas al tamaño de la imagen original para máxima calidad en fullscreen.
  // El área de cert a escala base es FW_FRAME * certRect.w (~875px). Si la imagen original
  // es mayor, escalamos proporcionalmente hasta 4096px de ancho máximo.
  const certBaseW = FW_FRAME * certRect.w;
  let FW = FW_FRAME;
  if (imgEl && imgEl.naturalWidth > 0) {
    const s = imgEl.naturalWidth / certBaseW;
    FW = Math.round(FW_FRAME * Math.max(1, Math.min(4096 / FW_FRAME, s)));
  }

  const frameRatioFs = item.frameRatio ?? (FW_FRAME / FH_FRAME);
  const VH = Math.max(1, Math.round(FW / frameRatioFs));

  const canvas = document.createElement('canvas');
  canvas.width = FW;
  canvas.height = VH;
  const ctx = canvas.getContext('2d');

  const cs = (item.certState?.wRel > 0) ? item.certState : null;

  let cx, cy, cw, ch;
  let crop = { t: 0, r: 0, b: 0, l: 0 };

  if (cs) {
    cx = cs.xRel * FW;
    cy = cs.yRel * VH;
    cw = cs.wRel * FW;
    ch = cs.hRel * VH;
    if (cs.crop) crop = cs.crop;
  } else {
    const autoCertW   = FW * certRect.w;
    const autoCertH   = VH * certRect.h;
    const autoCertOffX = (certRect.x + certRect.w * 0.5 - 0.5) * FW;
    const autoCertOffY = (0.5 - certRect.y - certRect.h * 0.5) * VH;

    cw = autoCertW;
    ch = autoCertH;
    cx = FW / 2 + autoCertOffX - cw / 2;
    cy = VH / 2 - autoCertOffY - ch / 2;
  }

  if (imgEl) {
    const iw = imgEl.naturalWidth;
    const ih = imgEl.naturalHeight;
    if (iw > 0 && ih > 0) {
      const srcX = crop.l * iw;
      const srcY = crop.t * ih;
      const srcW = (1 - crop.l - crop.r) * iw;
      const srcH = (1 - crop.t - crop.b) * ih;
      const dstX = cx + cw * crop.l;
      const dstY = cy + ch * crop.t;
      const dstW = cw * (1 - crop.l - crop.r);
      const dstH = ch * (1 - crop.t - crop.b);
      ctx.drawImage(imgEl, srcX, srcY, srcW, srcH, dstX, dstY, dstW, dstH);

      if (item.tipo !== 'mapa') {
        ctx.save();
        ctx.beginPath();
        ctx.rect(dstX, dstY, dstW, dstH);
        ctx.clip();
        aplicarTexturaPapelCertificado(ctx, FW, VH, 0.72);
        ctx.restore();
      }
    }
  }

  const off = document.createElement('canvas');
  off.width = FW;
  off.height = VH;
  const octx = off.getContext('2d');
  octx.drawImage(frameImg, 0, 0, FW, VH);
  octx.clearRect(
    Math.floor(FW * certRect.x),
    Math.floor(VH * certRect.y),
    Math.ceil(FW * certRect.w),
    Math.ceil(VH * certRect.h)
  );
  ctx.drawImage(off, 0, 0);

  if (item.tipo !== 'mapa') {
    aplicarReflejoVidrio(ctx, cx, cy, cw, ch, 0.66);
  }

  return canvas;
}